import { addDate } from 'src/helpers/date_helper';
import {
	findSelectraPeriodAt,
	getSelectraChartDays,
	getSelectraChartRangePeriod,
	getSelectraPeriodBgColor,
} from 'src/helpers/selectra_tariff_helpers';
import { CHART_VARIANTS_LIST } from '../ui/chart_menu_actions_helpers';
import { DEFAULT_CHART_HEIGHT } from './chart_config';
//Types
import type { ChartDataInterface } from 'src/types/Chart';
import type { PowerPricesData } from 'src/types/PowerPrices';
import type { SelectraPricePeriod, SelectraPricesResponse } from 'src/types/Selectra';

export const getTariffIntervalsChartConfig = (
	powerPrices: PowerPricesData,
	units: string,
	range: number,
	startWeekOnSun = false,
): ChartDataInterface => {
	const data = [],
		schedule = powerPrices.schedule,
		startDate = Date.UTC(2023, 0, 1, 0, 0, 0, 0),
		sunday = [],
		series = [];
	for (let i = 0; i < schedule.length; ++i) {
		if (range >= 0 && i != range) continue;
		const date = i > 0 ? addDate(startDate, 'd', i) : startDate;
		for (let j = 0; j < schedule[i].length; ++j) {
			const tariffID = schedule[i][j],
				tariff = powerPrices.tariffs[tariffID],
				dateset = {
					dt: addDate(date, 'i', j * 30).getTime(),
					value: tariff?.price || 0,
					name: tariff?.name || '',
					bgColor: tariff?.bgColor
						? tariff?.bgColor
						: !tariff?.bgColor && tariff?.price > 0
							? 'color-green'
							: undefined,
				};
			i == 0 ? sunday.push(dateset) : data.push(dateset);
		}
	}
	series.push({
		name: 'timeZone',
		data: startWeekOnSun ? [...sunday, ...data] : [...data, ...sunday],
		visible: true,
		style: 'tariff_intervals',
	});
	return {
		name: 'tariffIntervals',
		type: CHART_VARIANTS_LIST[3],
		units,
		rangePeriod: range == -1 ? 'week' : 'day',
		series,
		height: DEFAULT_CHART_HEIGHT,
	};
};

const SLOT_MS = 30 * 60 * 1000;

const buildSelectraDaySlots = (periods: SelectraPricePeriod[], dayStart: number) => {
	const data = [];

	for (let j = 0; j < 48; j++) {
		const slotTime = dayStart + j * SLOT_MS;
		const slotEnd = slotTime + SLOT_MS;
		const period = findSelectraPeriodAt(periods, slotTime, slotEnd);

		data.push({
			dt: slotTime,
			value: period?.price ?? 0,
			name: period?.name ?? '',
			bgColor: period ? getSelectraPeriodBgColor(period, periods) : undefined,
		});
	}

	return data;
};

export const getSelectraTariffChartConfig = (
	prices: SelectraPricesResponse | null,
	range: number,
): ChartDataInterface => {
	const periods = prices?.prices ?? [];
	const currency = prices?.currency ?? '';
	const units = currency ? `${currency}/kWh` : '/kWh';
	const days = getSelectraChartDays(periods);
	const data = [];

	if (range === -1) {
		for (const dayStart of days) {
			data.push(...buildSelectraDaySlots(periods, dayStart));
		}
	} else {
		data.push(...buildSelectraDaySlots(periods, range));
	}

	return {
		name: 'selectraTariffIntervals',
		type: CHART_VARIANTS_LIST[3],
		units,
		rangePeriod: getSelectraChartRangePeriod(range, days, periods),
		series: [
			{
				name: 'timeZone',
				data,
				visible: true,
				style: 'tariff_intervals',
			},
		],
		height: DEFAULT_CHART_HEIGHT,
	};
};
